import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, socioApi, type ClaimResult, type VestingStatus, type TokenHolding } from "@/api/client";
import { AppShell } from "@/components/AppShell";
import { formatTokenAmount } from "@/lib/format";

/**
 * Saca tokens vested do cronograma do sócio.
 *
 * Como `vesting.release()` é callable por qualquer um (tokens sempre
 * vão pro beneficiary cadastrado), o backend dispara a tx pagando o
 * gas com o deployer. O sócio nunca precisa de ETH na chain.
 */

export default function Claim() {
  const navigate = useNavigate();
  const [vesting, setVesting] = useState<VestingStatus | null>(null);
  const [token, setToken] = useState<TokenHolding | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<ClaimResult | null>(null);

  // Vesting é sempre do token principal do projeto. Pega símbolo/decimais
  // dele; se a carteira ainda não tem saldo, assume 18 (padrão ERC-20).
  const decimals = token?.decimals ?? 18;
  const symbol = token?.tokenSymbol ?? "tokens";
  const fmt = (v: string | undefined) => formatTokenAmount(v, decimals);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [v, b] = await Promise.all([
        socioApi.vesting(),
        socioApi.balances(),
      ]);
      setVesting(v);
      setToken(b.tokens[0] ?? null);
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onClaim() {
    setClaiming(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await socioApi.claim();
      setSuccess(result);
      await load();
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally {
      setClaiming(false);
    }
  }

  if (loading) return <AppShell><div className="p-8 text-center text-app-muted">Carregando...</div></AppShell>;

  const releasable = vesting?.releasable ? BigInt(vesting.releasable) : 0n;
  const hasReleasable = releasable > 0n;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto p-6">
        <Link to="/dashboard" className="text-sm underline text-app-muted hover:text-app-accent">
          ← dashboard
        </Link>
        <h1 className="text-2xl font-bold my-4">Reivindicar tokens</h1>

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 rounded p-3 mb-4 text-sm text-red-500 whitespace-pre-wrap">
            {error}
          </div>
        )}

        {success && (
          <div className="app-card border-green-500/40 bg-green-500/5 p-4 mb-4">
            <p className="font-semibold mb-2">✅ Claim realizado!</p>
            <p className="text-sm">
              <strong>{fmt(success.releasedNow)} {symbol}</strong> liberados pra sua wallet
            </p>
            <a target="_blank" rel="noreferrer"
               className="text-app-accent underline text-xs"
               href={`https://sepolia.basescan.org/tx/${success.txHash}`}>
              Ver no Basescan
            </a>
          </div>
        )}

        {!vesting?.hasVesting ? (
          <div className="app-card p-5">
            <p className="text-app-muted">
              Você ainda não tem vesting ativo. Aguarde o admin completar o deploy do projeto.
            </p>
            <button onClick={() => navigate("/dashboard")}
                    className="mt-4 text-app-accent underline">← voltar pro dashboard</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="app-card p-5">
              <h2 className="text-lg font-semibold mb-3">Saldo atual</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase text-app-muted">Em conta</p>
                  <p className="text-2xl font-semibold">
                    {fmt(token?.balance)} <span className="text-sm text-app-muted">{symbol}</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-app-muted">Voting power</p>
                  <p className="text-2xl font-semibold">{fmt(token?.votingPower)}</p>
                </div>
              </div>
            </div>

            <div className="app-card p-5">
              <h2 className="text-lg font-semibold mb-3">Cronograma de vesting</h2>
              <div className="space-y-2 text-sm">
                <Row label="Disponível pra sacar"
                     value={`${fmt(vesting.releasable)} ${symbol}`}
                     highlight={hasReleasable} />
                <Row label="Já sacado"
                     value={`${fmt(vesting.released)} ${symbol}`} />
                <Row label="Cliff"
                     value={`${vesting.cliffDuration ? Math.round(vesting.cliffDuration / 86400 / 30) : "?"} meses`} />
                <Row label="Duração total"
                     value={`${vesting.duration ? Math.round(vesting.duration / 86400 / 30) : "?"} meses`} />
              </div>

              {vesting.revoked && (
                <p className="text-red-500 text-sm mt-3">⚠️ Este vesting foi revogado</p>
              )}
            </div>

            <button
              onClick={onClaim}
              disabled={!hasReleasable || claiming || vesting.revoked}
              className="w-full bg-app-accent text-app-accent-text rounded py-3 font-semibold disabled:opacity-50 hover:opacity-90"
            >
              {claiming
                ? "Sacando..."
                : hasReleasable
                ? `Sacar ${fmt(vesting.releasable)} ${symbol}`
                : "Nada disponível pra sacar"}
            </button>

            {!hasReleasable && !vesting.revoked && (
              <p className="text-xs text-app-muted text-center">
                Tokens são liberados gradualmente após o cliff. Volte mais tarde.
              </p>
            )}

            <p className="text-xs text-app-muted text-center">
              Você não paga nada de gas — o sistema cobre o custo da rede.
            </p>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between border-b border-app-border last:border-0 py-1.5">
      <span className="text-app-muted">{label}:</span>
      <span className={highlight ? "font-bold text-app-accent" : ""}>{value}</span>
    </div>
  );
}
